// features/workspace/WorkspaceContext.tsx
import { createContext, useContext } from "react";
import type { Snippet } from "./snippets/SnippetContainer";
import type { Folder } from "./sidebar/SideBarTypes";

type RefLike<T> = { current: T };

export type RoomMemberDetails = {
  // id: string;
  role: 'owner' | 'admin' | 'member';
  name: string;
  canEdit: boolean;
  clientId: string;
  canCreate: boolean;
}


export type Member = {
  name: string;
  clientId: string;
  iconColor: string;
  role: string;
  id: string;
  canEdit: boolean;
  canCreate: boolean;
  isAdmin: boolean;
  isActive: boolean
}

export type roomDetails = {
  id: string;
  roomName: string;
  roomType: string;
  entranceCode: string;
  storagePlan: string;
  creatorClientId: string;
  creatorUserId?: string | null;
  totalStorage: number;
  bannedClients: string[];
  shareableLinkEnabled?: boolean | null;
  trackMembersEnabled?: boolean | null;
  maxMembersEnabled?: boolean | null;
  maxMembers?: number | null;
  defaultMemberRole?: "member" | "editor" | null;
}

export type RoomSettings = {
  shareableLinkEnabled: boolean;
  trackMembersEnabled: boolean;
  maxMembersEnabled: boolean;
  maxMembers: number | null;
  defaultMemberRole: "member" | "editor";
}

export type FileRef = {
  _id: string;
  roomId: string;
  folderParent?: string | null;
  key: string;
  name: string;
  mime?: string;
  size?: number;
  fileExtension?: string;
  fileType?: string;
  uploadStatus: "pending" | "uploaded" | "failed";
  etag?: string;
  uploaderClientId?: string;
  createdAt?: string;
  updatedAt?: string;
}


export type ActiveResourceId =
  | {
    resourceType: "snippet";
    id: Snippet["_id"];
  }
  | {
    resourceType: "file";
    id: FileRef["_id"];
  }
  | null;


export type OpenTab =
  | {
    resourceType: "snippet";
    data: Snippet;
  }
  | {
    resourceType: "file";
    data: FileRef;
  };


export type YourRole = "owner" | "admin" | "editor" | "member";

export type SBmodeTabs = "editor" | "filelist" | "members" | "settings"
export type MainUiMode = "editor" | "files"


export interface WorkspaceCtx {
  pendingOpenId: string | null;
  setPendingOpenId: React.Dispatch<React.SetStateAction<string | null>>;
  ctheme: string;
  prompt: boolean;
  yourRole: YourRole | null,
  setYourRole: React.Dispatch<React.SetStateAction<YourRole | null>>;
  setNewSnipPopup: (b: boolean) => void;
  newSnipPopup: boolean;
  roomStorage: number;
  setRoomStorage: React.Dispatch<React.SetStateAction<number>>;

  sbMode: SBmodeTabs;
  setSbMode: React.Dispatch<React.SetStateAction<SBmodeTabs>>;
  uiMode: MainUiMode;
  setUiMode: React.Dispatch<React.SetStateAction<MainUiMode>>;

  limitPopup: boolean;
  setLimitPopup: (b: boolean) => void;

  addNewPopup: boolean;
  setAddNewPopup: (b: boolean) => void;

  limitMessage: string | null;
  setLimitMessage: React.Dispatch<React.SetStateAction<string | null>>;

  roomDetails: roomDetails | null;
  roomSettings: RoomSettings;
  setRoomDetails: React.Dispatch<React.SetStateAction<roomDetails | null>>;
  memberDetails: RoomMemberDetails | null;
  setMemberDetails: React.Dispatch<React.SetStateAction<RoomMemberDetails | null>>;
  setcTheme: (t: string) => void;
  setPrompt: (b: boolean) => void;
  activeSnippetId: Snippet['_id'] | null;
  setActiveSnippetId: React.Dispatch<React.SetStateAction<Snippet['_id'] | null>>;
  openSnippets: Snippet[];
  setOpenSnippets: React.Dispatch<React.SetStateAction<Snippet[]>>;


  activeFileId: ActiveResourceId;
  setActiveFileId: React.Dispatch<React.SetStateAction<ActiveResourceId>>;
  activeFile: | { resourceType: "snippet"; data: Snippet }
  | { resourceType: "file"; data: FileRef }
  | null;

  openTabs: OpenTab[];
  setOpenTabs: React.Dispatch<React.SetStateAction<OpenTab[]>>;


  membersList: Member[];
  activeMembers: Member[];
  setMembersList: React.Dispatch<React.SetStateAction<Member[]>>;
  clientId: string | null;
  setClientId: React.Dispatch<React.SetStateAction<string | null>>;
  activeTerminal: boolean;
  setActiveTerminal: (b: boolean) => void;
  sideBarOpen: boolean;
  toggleSideBarOpen: (b: boolean) => void;
  activeSnippet: Snippet | null;
  Files: FileRef[];
  setFiles: React.Dispatch<React.SetStateAction<FileRef[]>>;
  Snippets: Snippet[];
  setSnippets: React.Dispatch<React.SetStateAction<Snippet[]>>;
  Folders: Folder[];
  setFolders: React.Dispatch<React.SetStateAction<Folder[]>>;
  globalNewSnipPopup: Boolean;
  setGlobalNewSnipPopup: (b: boolean) => void;
  globalNewFilePopup: Boolean;
  setGlobalNewFilePopup: (b: boolean) => void;
  globalNewFolderPopup: Boolean;
  setGlobalNewFolderPopup: (b: boolean) => void;
  downloadFileErrorPopup: boolean;
  setDownloadFileErrorPopup: (b: boolean) => void;

  // Tracks which rooms have already had their open tabs restored from the
  // per-room cookie THIS room visit. Lives on WorkspaceRoom (one instance per
  // room visit — remounts on Home<->Room navigation, not on uiMode toggles),
  // so SnippetContainer can safely remount when uiMode flips without
  // re-triggering the restore, while a real room re-entry still gets one.
  hydratedRoomIdsRef: RefLike<Set<string>>;
  roomSettingsInfoPopup: boolean;
  setRoomSettingsInfoPopup: (b: boolean) => void;
  qrPopup: boolean;
  setQrPopup: (b: boolean) => void;






}





export const WorkspaceContext = createContext<WorkspaceCtx | undefined>(undefined);





export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used within WorkspaceContext.Provider");
  return ctx;
};




